import { X } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";

import { SubscriptionPaymentMethodModal } from "features/payment-methods/components";
import useSubscriptionPaymentSource from "features/payment-methods/datahooks/useSubscriptionPaymentSource";

import { LinkedInLoginModal } from "common/components/LinkedInLogin";
import RelativeLink from "common/components/RelativeLink";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { formatShortDate } from "common/helpers/utils";
import useBannerVisibility from "common/hooks/useBannerVisibility";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import Banner from "../Banner";

function FreeTrialBanner() {
  const { subscription } = useSelectedWorkspaceContext();
  const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(false);

  const { subscriptionPaymentSource, isLoadingSubscriptionPaymentSource } =
    useSubscriptionPaymentSource(subscription.id, false);

  const nextBilling = formatShortDate(subscription.next_billing * 1000);

  let innerContent: ReactNode;
  if (isLoadingSubscriptionPaymentSource) {
    innerContent = (
      <>
        <Skeleton className="h-4 mb-0.5 lg:h-5 lg:hidden w-full" />
        <Skeleton className="h-4 lg:h-5 w-1/3" />
      </>
    );
  } else if (subscriptionPaymentSource) {
    innerContent = (
      <p>
        <span className="text-sm-bold">Free trial active:&nbsp;</span>
        Your subscription will activate on&nbsp;
        <span className="text-sm-bold">{nextBilling}</span>
      </p>
    );
  } else {
    innerContent = (
      <>
        <p className="lg:truncate">
          <span className="text-sm-bold">Trial ends on {nextBilling}. </span>
          Add a payment method to keep your access after the trial
        </p>

        <Button
          variant="link-purple"
          className="ml-auto lg:border-l border-purple-200"
          onClick={() => setIsAddingPaymentMethod(true)}
        >
          Add payment method
        </Button>

        <SubscriptionPaymentMethodModal
          isOpen={isAddingPaymentMethod}
          setIsOpen={setIsAddingPaymentMethod}
          subscriptionId={subscription.id}
          mixpanelSource="trial_banner"
        />
      </>
    );
  }

  return <Banner variant="gradient">{innerContent}</Banner>;
}

function ConnectAccountBanner() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <Banner variant="danger">
        <p className="lg:truncate">
          <span className="text-sm-bold">Connect a LinkedIn account: </span>
          Add a LinkedIn account to unlock Campaigns, Leads and Inbox
        </p>

        <Button
          variant="link-black"
          className="ml-auto lg:border-l border-black-200"
          onClick={() => setIsDialogOpen(true)}
        >
          Add a LinkedIn account
        </Button>
      </Banner>

      <LinkedInLoginModal
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
}

function AccountDisconnectedBanner() {
  const isDesktop = useTwBreakpoint("lg");

  const { isVisible, setIsVisible } = useBannerVisibility(
    "accountDisconnectedBanner",
  );

  const textComponent = (
    <p className="lg:truncate">
      <span className="text-sm-bold">Account disconnected: </span>
      Reconnect your LinkedIn account to use campaigns
    </p>
  );

  if (isVisible) {
    return (
      <Banner variant="default">
        {isDesktop ? (
          textComponent
        ) : (
          <div className="flex gap-x-2 justify-between">
            {textComponent}

            <Button
              variant="ghost-black"
              intent="iconOnly"
              size="sm"
              onClick={() => setIsVisible(false)}
            >
              <X />
            </Button>
          </div>
        )}

        <Button
          variant="link-black"
          className="ml-auto lg:border-x border-black-200"
          asChild
        >
          <RelativeLink to="accounts">Go to accounts</RelativeLink>
        </Button>

        <RenderIf condition={isDesktop}>
          <button type="button" onClick={() => setIsVisible(false)}>
            <X />
          </button>
        </RenderIf>
      </Banner>
    );
  }
}

function PaymentMethodBanner() {
  const isDesktop = useTwBreakpoint("lg");
  const { subscription } = useSelectedWorkspaceContext();
  const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(false);

  const { isVisible, setIsVisible } = useBannerVisibility(
    "paymentMethodBanner",
  );

  const textComponent = (
    <p className="lg:truncate">
      <span className="text-sm-bold">No payment method: </span>
      Add payment method to avoid subscription downtime on your next billing
      date
    </p>
  );

  if (isVisible) {
    return (
      <Banner variant="warning">
        {isDesktop ? (
          textComponent
        ) : (
          <div className="flex gap-x-2 justify-between">
            {textComponent}

            <Button
              variant="ghost-black"
              intent="iconOnly"
              size="sm"
              onClick={() => setIsVisible(false)}
            >
              <X />
            </Button>
          </div>
        )}

        <Button
          variant="link-black"
          className="ml-auto lg:border-x border-black-200"
          onClick={() => setIsAddingPaymentMethod(true)}
        >
          Update payment method
        </Button>

        <RenderIf condition={isDesktop}>
          <button type="button" onClick={() => setIsVisible(false)}>
            <X />
          </button>
        </RenderIf>

        <SubscriptionPaymentMethodModal
          isOpen={isAddingPaymentMethod}
          setIsOpen={setIsAddingPaymentMethod}
          subscriptionId={subscription.id}
          mixpanelSource="no_payment"
        />
      </Banner>
    );
  }
}

export default function Banners() {
  const { pathname } = useLocation();

  const {
    subscription,
    id,
    accounts,
    accounts_count: accountsCount,
    lifetime_license_count: lifetimeLicenseCount,
    role,
  } = useSelectedWorkspaceContext();

  const { subscriptionPaymentSource, isLoadingSubscriptionPaymentSource } =
    useSubscriptionPaymentSource(subscription?.id, false);

  const isLifetimeDeal = lifetimeLicenseCount > 0;
  const isAccountsPage = pathname.includes(`/${id}/accounts`);

  if (subscription && subscription.status === "in_trial") {
    return <FreeTrialBanner />;
  }

  if (accountsCount === 0) {
    return <ConnectAccountBanner />;
  }

  if (accounts.some(({ state }) => state !== "LoggedIn") && !isAccountsPage) {
    return <AccountDisconnectedBanner />;
  }

  if (
    subscription &&
    !isLoadingSubscriptionPaymentSource &&
    !subscriptionPaymentSource &&
    !isLifetimeDeal
  ) {
    return (
      <RestrictedComponent visibleFor="owner">
        <PaymentMethodBanner />
      </RestrictedComponent>
    );
  }
}
